/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import * as Sentry from '@sentry/browser';
import { BlendBridge } from 'blb-types/blendBridge';

import { MeContext } from 'blb-contexts/Me';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { getConfig } from 'blb-utils/config';
import { getPersona } from 'blb-persona/index';
import { getUserGroups, getOutstandingInvitations } from 'blb-api/users';
import { theme as grommetTheme } from 'blb-components/GrommetComponents';

import CreateTeamModal from './CreateTeamModal/CreateTeamModal';
import TeamsPageHeader from './TeamsPageHeader';
import TeamsPageContent from './TeamsPageContent';
import { StyledAlchemy } from './styles';
import TeamsInvitationModal from './TeamsInvitationModal';

export namespace TeamsPage {
  export interface Props {}
}

export const TeamsPage: React.FC<TeamsPage.Props> = ({}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [pageError, setPageError] = useState<Error>();
  const [teams, setTeams] = useState<BlendBridge.API.SearchGroupRecord[]>([]);
  const [invitations, setInvitations] = useState<BlendBridge.API.GroupInvitationResponse[]>([]);
  const [isInvitationsModalOpen, setIsInvitationsModalOpen] = useState<boolean>(false);

  const me = useContext(MeContext)!;
  const { isTeamsUIEnabled } = getConfig();

  const handleInvitationsModalClose = (shouldRefetchTeams?: boolean) => {
    setIsInvitationsModalOpen(false);
    if (shouldRefetchTeams) {
      void fetchTeams();
    }
  };

  const fetchTeams = async () => {
    if (isTeamsUIEnabled) {
      try {
        const response = await getUserGroups(me.principalId);
        setTeams(response.data);
      } catch (error) {
        setPageError(error as Error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchInvitations = async () => {
    try {
      const invitations = await getOutstandingInvitations(me.principalId);
      setInvitations(invitations);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    fetchInvitations();
    fetchTeams();
  }, []);

  useEffect(() => {
    setIsInvitationsModalOpen(invitations.length > 0);
  }, [invitations]);

  if (!isTeamsUIEnabled) {
    return <Redirect to={getPersona(me.principalType).getAllowedRoutes(me).defaultLandingPath} />;
  }

  if (isLoading) {
    return <CenteredLoadingSpinner />;
  }

  return (
    <StyledAlchemy grommetTheme={grommetTheme}>
      <TeamsPageHeader setIsCreateModalOpen={setIsCreateModalOpen} />
      {isInvitationsModalOpen && (
        <TeamsInvitationModal
          handleClose={() => handleInvitationsModalClose(true)}
          invitations={invitations}
          isOpen={isInvitationsModalOpen}
        />
      )}
      <TeamsPageContent error={pageError} refetchTeams={fetchTeams} teams={teams} />
      <CreateTeamModal
        handleClose={async () => {
          setIsCreateModalOpen(false);
          await fetchTeams();
        }}
        isOpen={isCreateModalOpen}
      />
    </StyledAlchemy>
  );
};
